// Changes during a round (Lane 1331, Phase 2 slice 2E-1). On a published
// event the buyer starts a change, edits the parts and terms on the change
// draft (the Parts and Notes and terms tabs), then reviews and publishes it.
// Suppliers see nothing until the publish. Each change gets a policy for the
// offers already received; the server says which policies are allowed, what
// each pick does to the offers, and refuses a publish if the change or the
// offers moved after the review.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AmendmentChanges, EventDetails, PartsTable, formatDueAt } from '../sourcing-portal/portalView';
import { STALE_MESSAGE } from './sourcingApi';

export const POLICY_TEXT = {
  still_valid: 'Offers still count',
  acknowledge: 'Supplier must acknowledge',
  resubmit: 'Supplier must quote again',
};
const TERM_NAMES = {
  currency: 'Currency', payment_terms: 'Payment terms', incoterms: 'Delivery terms (Incoterms)',
  offer_validity_days: 'Offers valid for (days)', delivery_notes: 'Delivery notes',
  other_terms: 'Other terms', description: 'What suppliers read about this event',
};
const FIELD_TEXT = { quantity: 'volume', unit: 'unit', description: 'name', notes: 'notes' };
const RELOAD_CODES = ['changes_changed', 'offers_changed', 'stale_revision', 'no_amendment_draft',
  'event_not_editable'];

function errorText(e) {
  switch (e.code) {
    case 'changes_changed': return 'The change was edited after this review. Review it again.';
    case 'offers_changed': return 'The offers changed while this screen was open. Review again.';
    case 'response_window': return e.message;
    case 'round_closed': return 'The round is closed or past its due time. Open the next round or extend the due time first.';
    case 'stale_revision': return STALE_MESSAGE;
    case 'invalid_policies': return 'Pick an allowed choice for every change.';
    default: return e.message;
  }
}

function shortText(value) {
  if (value === null || value === undefined || value === '') return 'none';
  const text = String(value);
  return text.length > 80 ? `${text.slice(0, 80)}…` : text;
}

function describe(change) {
  if (change.kind === 'term') {
    return `${TERM_NAMES[change.field] || change.field}: ${shortText(change.from)} to ${shortText(change.to)}`;
  }
  if (change.kind === 'part') {
    if (change.sub_kind === 'added') return `Part ${change.part_number} added`;
    if (change.sub_kind === 'removed') return `Part ${change.part_number} removed. Its offers stop counting.`;
    const bits = (change.fields || []).map((f) => {
      if (f === 'quantity') return `volume ${shortText(change.from && change.from.volume)} to ${shortText(change.to && change.to.volume)}`;
      if (f === 'unit') return `unit ${shortText(change.from && change.from.unit)} to ${shortText(change.to && change.to.unit)}`;
      return FIELD_TEXT[f] || f;
    });
    return `Part ${change.part_number}: ${bits.join(', ')}`;
  }
  if (change.kind === 'document') {
    const side = change.to || change.from || {};
    // The name suppliers see when the buyer set one (Lane 1440).
    const name = side.display_name || side.file_name || change.internal_name || 'A document';
    return `${name}: ${change.sub_kind === 'added' ? 'added' : change.sub_kind === 'removed' ? 'removed' : 'changed'}`
      + (change.supplier_visible === false ? ' (not shown to suppliers)' : '');
  }
  return change.change_id;
}

function plural(n, one, many) {
  return `${n.toLocaleString()} ${n === 1 ? one : many}`;
}

export default function AmendmentPanel({ api, eventId, summary, onChanged }) {
  const amendment = summary.amendment;
  const [reviewing, setReviewing] = useState(false);
  const [review, setReview] = useState(null);
  const [picks, setPicks] = useState({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [done, setDone] = useState('');

  // Only the newest review answer may fill the screen: an older, slower one
  // must never replace the buyer's latest pick or its impact (Codex 2E-1 r1).
  const requestSeq = useRef(0);
  const load = useCallback((withPicks) => {
    const seq = ++requestSeq.current;
    return api.amendmentReview(eventId, withPicks)
      .then((r) => {
        if (seq !== requestSeq.current) return;
        setReview(r);
        setPicks(Object.fromEntries(r.changes.filter((c) => c.allowed.length).map((c) => [c.change_id, c.pick])));
        setError('');
      })
      .catch((e) => { if (seq === requestSeq.current) setError(errorText(e)); });
  }, [api, eventId]);

  // A change made on the Parts or Terms tab moves the revision: the review on
  // screen is out of date, so it is read again.
  useEffect(() => { if (reviewing && amendment) load(); }, [reviewing, amendment, summary.revision, load]);

  const run = async (fn, after) => {
    setBusy(true);
    setError('');
    try {
      const r = await fn();
      await onChanged();
      if (after) after(r);
    } catch (e) {
      setError(errorText(e));
      if (RELOAD_CODES.includes(e.code)) { await onChanged(); if (reviewing) load(); }
    } finally {
      setBusy(false);
    }
  };

  if (!['open', 'closed'].includes(summary.status)) return null;

  if (!amendment) {
    return (
      <div className="srce-bulk">
        <span className="srce-mut">To change the parts or the terms after publish, start a change. Suppliers see nothing until you publish it.</span>
        <button type="button" className="srce-btn" disabled={busy}
          onClick={() => run(() => api.amendmentAction(eventId, 'start', {}, summary.revision),
            () => setDone(''))}>Change the event</button>
        {done && <span className="srce-good">{done}</span>}
        {error && <span className="srce-error" role="alert">{error}</span>}
      </div>
    );
  }

  const choose = (changeId, policy) => {
    const next = { ...picks, [changeId]: policy };
    setPicks(next);
    setReview((r) => (r ? { ...r, blocks: [...r.blocks, { code: 'loading', message: '' }] } : r));
    load(next);
  };
  // Publish waits for the answer to the latest pick (loading is true while a
  // newer review request is in flight).
  const blocked = !review || review.blocks.length > 0;
  const publish = () => run(() => api.amendmentAction(eventId, 'publish', {
    expected_offers_digest: review.expected_offers_digest,
    changes_digest: review.changes_digest,
    policies: Object.entries(picks).map(([change_id, policy]) => ({ change_id, policy })),
  }, summary.revision), (r) => {
    setReviewing(false);
    setReview(null);
    const n = r.result.notices || 0;
    setDone(r.result.amendment_number
      ? `Change ${r.result.amendment_number} published. ${plural(n, 'supplier contact was', 'supplier contacts were')} told.`
      : 'Change published. Suppliers were not told: nothing they see changed.');
  });

  return (
    <section className="srce-panel" aria-label="Change in progress">
      <div className="srce-row">
        <span className="srce-strong">You are changing the event.</span>
        <span className="srce-mut">Suppliers see nothing until you publish the change. Edit the parts on the Parts tab, and the terms and documents under Change the event.
          {' '}{plural(amendment.change_count, 'change', 'changes')} so far.</span>
        <span className="srce-push srce-row">
          <button type="button" className="srce-btn srce-btn--primary" disabled={busy}
            onClick={() => setReviewing((v) => !v)}>{reviewing ? 'Hide the review' : 'Review and publish'}</button>
          {!confirmDiscard && (
            <button type="button" className="srce-btn srce-btn--quiet" disabled={busy}
              onClick={() => setConfirmDiscard(true)}>Discard the change</button>
          )}
          {confirmDiscard && (
            <span className="srce-row">
              <span className="srce-crit">Discard every edit in this change?</span>
              <button type="button" className="srce-btn srce-btn--primary" disabled={busy}
                onClick={() => { setConfirmDiscard(false); run(() => api.amendmentAction(eventId, 'discard', {}, summary.revision), () => { setReviewing(false); setReview(null); }); }}>Yes, discard</button>
              <button type="button" className="ll-clr" onClick={() => setConfirmDiscard(false)}>Keep it</button>
            </span>
          )}
        </span>
      </div>
      {error && <div className="srce-error" role="alert">{error}</div>}
      {reviewing && !review && !error && <div className="ll-loading">Loading the review…</div>}
      {reviewing && review && (
        <div className="srce-gap">
          {review.blocks.filter((b) => b.message).map((b) => <div key={b.code} className="srce-error" role="alert">{b.message}</div>)}
          {review.warnings.map((w) => <div key={w.code} className="srce-warn">{w.message}</div>)}
          {review.changes.length > 0 && (
            <div className="srce-gap">
              <div className="srce-strong">What changes, and what happens to the offers already received</div>
              <ul className="srce-list">
                {review.changes.map((c) => (
                  <li key={c.change_id} className="srce-row">
                    <span>{describe(c)}</span>
                    {c.allowed.length > 1 && (
                      <select className="ll-sel srce-push" value={picks[c.change_id] || ''} aria-label={`Offers after: ${describe(c)}`}
                        onChange={(e) => choose(c.change_id, e.target.value)}>
                        {c.allowed.map((p) => <option key={p} value={p}>{POLICY_TEXT[p]}</option>)}
                      </select>
                    )}
                    {c.allowed.length === 1 && <span className="srce-mut srce-push">{POLICY_TEXT[c.allowed[0]]}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="srce-gap">
            <div className="srce-strong">Offers affected</div>
            <div>{review.impact.needs_acknowledgement.offers
              ? `${plural(review.impact.needs_acknowledgement.offers, 'offer', 'offers')} from ${plural(review.impact.needs_acknowledgement.suppliers, 'supplier', 'suppliers')} will count again only after the supplier acknowledges.`
              : 'No offer needs an acknowledgement.'}</div>
            <div>{review.impact.needs_resubmission.offers
              ? `${plural(review.impact.needs_resubmission.offers, 'offer', 'offers')} from ${plural(review.impact.needs_resubmission.suppliers, 'supplier', 'suppliers')} will stop counting until the supplier quotes again.`
              : 'No offer needs a new quote.'}</div>
            {review.impact.not_in_scope.offers > 0 && (
              <div>{plural(review.impact.not_in_scope.offers, 'offer is', 'offers are')} for parts you removed and will stop counting.</div>
            )}
            <div className="srce-mut">Quotes are due {formatDueAt(review.due_at)}.{review.needs_action
              ? ` Suppliers need at least ${review.min_response_hours} hours to respond to this change.` : ''}</div>
          </div>
          <div className="srce-gap">
            <div className="srce-strong">What suppliers will read</div>
            <div className="srce-mut">Exactly what the supplier portal shows after you publish.</div>
            <div className="srce-preview">
              <AmendmentChanges changes={review.supplier_view.amendment.changes} />
              {review.supplier_view.amendment.total > review.supplier_view.amendment.changes.length && (
                <div className="srce-mut">
                  and {(review.supplier_view.amendment.total - review.supplier_view.amendment.changes.length).toLocaleString()} more
                  {' '}changes, shown to suppliers page by page.
                </div>
              )}
              <div className="srce-strong srce-gap">The event after the change</div>
              <EventDetails event={review.supplier_view.event} />
              {(review.supplier_view.documents || []).length > 0 && (
                <>
                  <div className="srce-strong srce-gap">Documents suppliers will see</div>
                  <ul className="srce-list">
                    {review.supplier_view.documents.map((d) => (
                      <li key={d.document_id}>{d.file_name}{d.informational ? ' (for information only)' : ''}</li>
                    ))}
                  </ul>
                </>
              )}
              <div className="srce-strong srce-gap">The parts after the change</div>
              <PartsTable items={review.supplier_view.parts.items} />
              {review.supplier_view.parts.total > review.supplier_view.parts.items.length && (
                <div className="srce-mut">
                  First {review.supplier_view.parts.items.length.toLocaleString()} of {review.supplier_view.parts.total.toLocaleString()} parts.
                </div>
              )}
            </div>
          </div>
          <div className="srce-gap">
            <div className="srce-strong">Who is told</div>
            <div>{review.recipients.length
              ? `${review.recipients.join(', ')} ${review.recipients.length === 1 ? 'gets' : 'get'} a notice that the event changed.`
              : 'No supplier has joined yet, so no one is told.'}</div>
          </div>
          <div className="srce-actions">
            <button type="button" className="srce-btn srce-btn--primary" disabled={busy || blocked}
              onClick={publish}>Publish the change</button>
          </div>
        </div>
      )}
    </section>
  );
}
